import { isEpisodeExcluded, type EpisodeFileResp, type ExtraArtifactFile } from "@/api";

/**
 * 다운로드 대상 산정. canonical episode · meta · extra 세 목록을 한 목록으로 합친다.
 *
 * 세 종류를 모두 받으면 Dataset 의 전체 파일이다(BE `docs/specs/dataset-file-download.md` §1).
 * object key 레이아웃은 셋 다 `<root_prefix>/artifact_sources/<source_uuid>/<relative_path>` 로
 * 같다. 그래서 `relative_path` 하나로 대상 디렉터리 트리를 복원할 수 있다.
 */

export type DownloadFileKind = "episode" | "meta" | "extra";

export type DownloadEntry = {
  uuid: string;
  kind: DownloadFileKind;
  // source root 기준 파일명 포함 전체 경로. 대상 폴더 아래 이 경로 그대로 복원한다.
  relativePath: string;
  // 재개 판정(이미 받은 파일 skip)에 쓴다. BE 가 모르면 null 이고 그때는 skip 하지 않는다.
  sizeBytes: number | null;
  // 목록에 인라인된 signed GET URL. 조건을 충족하지 않으면 null 이고, 단건 재서명이 필요하다.
  url: string | null;
  expiresAt: string | null;
};

export type DownloadSelection = {
  // 폴더 스코프(`DirectoryFolder.path`). null·'' 이면 데이터셋 전체.
  prefix: string | null;
  // excluded 에피소드의 파일도 받을지. meta/extra 는 에피소드 스코프가 아니므로 영향이 없다.
  includeExcluded: boolean;
};

export type DownloadPlan = {
  // relative_path 오름차순. BE 목록은 id 순이므로 트리 순서는 클라이언트가 만든다(스펙 §2.1).
  entries: DownloadEntry[];
  // 같은 relative_path 가 둘 이상 나온 경우. 먼저 온 것만 남기고 나머지를 여기 모은다.
  // 그대로 쓰면 뒤 파일이 앞 파일을 덮어써 조용히 파일이 사라진다. canonical 은 매핑 스코프이고
  // meta/extra 는 anchor 스코프다. 그래서 서로 다른 source 가 섞인 Dataset 에서 이론상 겹칠 수 있다.
  conflicts: DownloadEntry[];
};

export type DownloadSourceFiles = {
  episodeFiles: readonly EpisodeFileResp[];
  metaFiles: readonly ExtraArtifactFile[];
  extraFiles: readonly ExtraArtifactFile[];
};

export function buildDownloadPlan(
  files: DownloadSourceFiles,
  selection: DownloadSelection,
): DownloadPlan {
  const candidates = [
    ...episodeEntries(files.episodeFiles, selection.includeExcluded),
    ...extraEntries(files.metaFiles, "meta"),
    ...extraEntries(files.extraFiles, "extra"),
  ]
    .filter((entry) => matchesPrefix(entry.relativePath, selection.prefix))
    .sort((left, right) => left.relativePath.localeCompare(right.relativePath));

  const entries: DownloadEntry[] = [];
  const conflicts: DownloadEntry[] = [];
  const claimed = new Set<string>();
  for (const entry of candidates) {
    if (claimed.has(entry.relativePath)) {
      conflicts.push(entry);
      continue;
    }
    claimed.add(entry.relativePath);
    entries.push(entry);
  }
  return { entries, conflicts };
}

/** 대상 총 바이트. `size_bytes` 를 모르는 파일이 있으면 그만큼 적게 추정한다. */
export function totalDownloadBytes(entries: readonly DownloadEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.sizeBytes ?? 0), 0);
}

// orphan(`relative_path === null`)은 물리 위치를 도출할 수 없으므로 다운로드 대상이 아니다(스펙 §1).
function episodeEntries(
  files: readonly EpisodeFileResp[],
  includeExcluded: boolean,
): DownloadEntry[] {
  const entries: DownloadEntry[] = [];
  for (const file of files) {
    if (file.relative_path === null) continue;
    if (!includeExcluded && isEpisodeExcluded(file.episode_status)) continue;
    entries.push({
      uuid: file.uuid,
      kind: "episode",
      relativePath: file.relative_path,
      sizeBytes: file.size_bytes,
      url: file.original_url,
      expiresAt: file.original_url_expires_at,
    });
  }
  return entries;
}

function extraEntries(
  files: readonly ExtraArtifactFile[],
  kind: Extract<DownloadFileKind, "meta" | "extra">,
): DownloadEntry[] {
  return files.map((file) => ({
    uuid: file.uuid,
    kind,
    relativePath: file.relative_path,
    sizeBytes: file.size_bytes,
    url: file.download_url,
    expiresAt: file.download_url_expires_at,
  }));
}

// 폴더 스코프 판정. 경계에서 '/' 를 요구한다. prefix 'action' 이 'action_left/…' 와
// 일치하면 고르지 않은 폴더도 함께 받는다.
function matchesPrefix(relativePath: string, prefix: string | null): boolean {
  if (prefix === null || prefix === "") return true;
  const normalized = prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
  return relativePath === normalized || relativePath.startsWith(normalized + "/");
}
